import { useEffect, useRef } from 'react';
import { fontShorthand, fontSpecFor } from '../geometry/drawable';
import type { BoardView, TextElement } from '../scene/types';
import { worldToScreen } from '../scene/view';

interface Props {
  readonly element: TextElement;
  readonly view: BoardView;
  readonly onChange: (text: string) => void;
  readonly onCommit: () => void;
}

/**
 * A real textarea over the canvas, so typing, selection, IME and screen readers
 * all behave normally. It uses the identical font shorthand and line height as
 * the painter; otherwise the caret and the rendered wrap visibly disagree.
 */
export function TextOverlay({ element, view, onChange, onCommit }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.focus();
    node.setSelectionRange(node.value.length, node.value.length);
  }, [element.id]);

  const font = fontSpecFor(element.fontFamily, element.fontSize);
  const topLeft = worldToScreen(view, { x: element.x, y: element.y });
  const width = Math.max(element.width, font.size * 2) * view.zoom;
  const height = Math.max(element.height, font.lineHeight) * view.zoom;

  return (
    <textarea
      ref={ref}
      className="text-editor"
      aria-label="Edit text"
      value={element.text}
      spellCheck={false}
      wrap={element.containerId ? 'soft' : 'off'}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onCommit}
      onKeyDown={(event) => {
        // Escape and Cmd/Ctrl+Enter finish; plain Enter inserts a line break.
        if (event.key === 'Escape' || (event.key === 'Enter' && (event.metaKey || event.ctrlKey))) {
          event.preventDefault();
          event.stopPropagation();
          onCommit();
          return;
        }
        // Everything else must not reach the global shortcut handler.
        event.stopPropagation();
      }}
      style={{
        left: `${topLeft.x}px`,
        top: `${topLeft.y}px`,
        width: `${width}px`,
        height: `${height}px`,
        font: fontShorthand(font),
        fontSize: `${font.size * view.zoom}px`,
        lineHeight: `${font.lineHeight * view.zoom}px`,
        color: element.strokeColor,
        textAlign: element.textAlign,
        transform: element.angle === 0 ? undefined : `rotate(${(element.angle * 180) / Math.PI}deg)`,
        transformOrigin: 'center center',
      }}
    />
  );
}
